import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { FilterBar } from '@/components/composed/FilterBar';
import { Button } from '@/components/ui/button';
import {
  ProviderTable,
  ProviderFormDialog,
  providerListQuery,
} from '@/features/providers';
import type { Provider, ProviderListParams } from '@/lib/api/domains/providers';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  sort: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute('/_auth/providers/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(providerListQuery(toParams(deps))),
  component: ProvidersPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function toParams(search: z.infer<typeof searchSchema>): ProviderListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
}

function ProvidersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useAuthStore((s) => s.session);
  const [createOpen, setCreateOpen] = useState(false);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  usePageMeta({ title: 'Providers' });

  // Row/edit open the full detail page (providers use a sectioned detail view,
  // not an inspect sheet), landing on the first section.
  function openProvider(provider: Provider) {
    void navigate({
      to: '/providers/$providerId',
      params: { providerId: provider.id },
    });
  }

  return (
    <Page bleed>
      <PageHeader
        title="Providers"
        actions={
          canEdit(session) ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              New provider
            </Button>
          ) : null
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by name…"
          onClear={
            search.q ? () => setSearch({ q: undefined, page: 1 }) : undefined
          }
        />
        <ProviderTable
          params={toParams(search)}
          onPageChange={(page) => setSearch({ page })}
          onRowClick={openProvider}
          onEdit={openProvider}
        />
      </Section>

      <ProviderFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}
